'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
// Using @iconify/react for Material Symbols icons (savings, diamond, calculate, etc.) — no lucide-react equivalents
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useRTL } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger);

// Simplified Counter Hook
function useAnimatedCounter(targetValue: number, duration: number = 2000) {
    const [displayValue, setDisplayValue] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const rafIdRef = useRef<number | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, []);

    const animate = useCallback(() => {
        if (isAnimating) return;

        setIsAnimating(true);
        setDisplayValue(0);

        const startTime = Date.now();
        const startValue = 0;

        const updateCounter = () => {
            if (!mountedRef.current) return;

            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = Math.floor(startValue + (targetValue - startValue) * easeOutQuart);

            setDisplayValue(currentValue);

            if (progress < 1) {
                rafIdRef.current = requestAnimationFrame(updateCounter);
            } else {
                rafIdRef.current = null;
                setIsAnimating(false);
            }
        };

        rafIdRef.current = requestAnimationFrame(updateCounter);
    }, [isAnimating, duration, targetValue]);

    const reset = useCallback(() => {
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
        setDisplayValue(0);
        setIsAnimating(false);
    }, []);

    return { displayValue, animate, reset, isAnimating };
}

const LOCALE_MAP: Record<string, string> = {
    ar: 'ar-EG',
    fa: 'fa-IR',
};

function resolveLocale(locale: string): string {
    return LOCALE_MAP[locale] || locale;
}

function normalizeNumberString(formatted: string): string {
    return formatted
        .replace(/٬/g, ',')   // Arabic thousands separator
        .replace(/٫/g, '.')   // Arabic decimal separator
        .replace(/٪/g, '%');  // Arabic percent sign
}

function formatNumberForLocale(num: number, locale: string) {
    const formatted = num.toLocaleString(resolveLocale(locale));
    return normalizeNumberString(formatted);
}

// Animated Price Range Component
function AnimatedPriceRange({
    min,
    max,
    className = "",
    prefix = "$",
    isRTL,
    locale
}: {
    min: number;
    max: number;
    className?: string;
    prefix?: string;
    isRTL: boolean;
    locale: string;
}) {
    const minCounter = useAnimatedCounter(min, 1500);
    const maxCounter = useAnimatedCounter(max, 1500);

    return (
        <motion.div
            className={className}
            onViewportEnter={() => {
                minCounter.animate();
                maxCounter.animate();
            }}
            viewport={{ once: true, margin: "-100px" }}
            dir={isRTL ? "rtl" : "ltr"}
        >
            {isRTL ?
                `${formatNumberForLocale(maxCounter.displayValue, locale)}+ - ${formatNumberForLocale(minCounter.displayValue, locale)} ${prefix}` :
                `${prefix}${formatNumberForLocale(minCounter.displayValue, locale)} - ${prefix}${formatNumberForLocale(maxCounter.displayValue, locale)}+`
            }
        </motion.div>
    );
}

// Animated Single Price Component
function AnimatedPrice({
    value,
    className = "",
    prefix = "$",
    isRTL,
    locale
}: {
    value: number;
    className?: string;
    prefix?: string;
    isRTL: boolean;
    locale: string;
}) {
    const counter = useAnimatedCounter(value, 1500);

    return (
        <motion.div
            className={className}
            onViewportEnter={() => {
                counter.animate();
            }}
            viewport={{ once: true, margin: "-100px" }}
            dir={isRTL ? "rtl" : "ltr"}
        >
            {isRTL ?
                `${formatNumberForLocale(counter.displayValue, locale)} ${prefix}` :
                `${prefix}${formatNumberForLocale(counter.displayValue, locale)}`
            }
        </motion.div>
    );
}

// Animated Money Collection Component
function AnimatedMoneySavings({
    percentage,
    savingsAmount = "80%",
    isRTL,
    savedLabel
}: {
    percentage: number;
    savingsAmount?: string;
    isRTL: boolean;
    savedLabel: string;
}) {
    const [isAnimating, setIsAnimating] = useState(false);
    const [billsCollected, setBillsCollected] = useState(0);
    const [maxBills, setMaxBills] = useState(8);

    // Fewer bills on narrow viewports so the strip doesn't overflow the card.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(min-width: 640px)');
        const update = () => setMaxBills(mq.matches ? 8 : 5);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    useEffect(() => {
        if (isAnimating) {
            const interval = setInterval(() => {
                setBillsCollected(prev => {
                    if (prev >= maxBills) {
                        clearInterval(interval);
                        return maxBills;
                    }
                    return prev + 1;
                });
            }, 50);

            return () => clearInterval(interval);
        } else {
            setBillsCollected(0);
        }
    }, [isAnimating]);

    return (
        <motion.div
            onViewportEnter={() => {
                setIsAnimating(true);
            }}
            onViewportLeave={() => {
                setIsAnimating(false);
            }}
            viewport={{ once: false, margin: "-50px" }}
            className="relative bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-900/20 rounded-2xl border border-green-200/50 dark:border-green-800/30 overflow-hidden flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 p-4 sm:p-0 sm:h-16 sm:pr-4"
        >
            {/* Background money pattern */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
                <div className="w-full h-full opacity-20" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Cpath d='M10 0L5 5V15L10 20L15 15V5L10 0Z' fill='%23059669'/%3E%3C/svg%3E")`
                }}></div>
            </div>

            {/* Animated bills floating in. On mobile this takes a dedicated
                row above the badge; on desktop it shares the 64px bar. */}
            <div className={cn(
                "relative flex items-center h-8 sm:h-full sm:flex-1 sm:absolute sm:inset-y-0",
                isRTL ? "justify-end sm:right-28" : "justify-start sm:left-4"
            )}>
                {Array.from({ length: maxBills }).map((_, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -50, y: -20, rotate: -15 }}
                        animate={{
                            opacity: index < billsCollected ? 1 : 0,
                            x: index < billsCollected ? index * 8 : -50,
                            y: index < billsCollected ? 0 : -20,
                            rotate: index < billsCollected ? 0 : -15,
                        }}
                        transition={{
                            duration: 0.6,
                            delay: index * 0.2,
                            ease: "easeOut"
                        }}
                        className="w-12 h-6 bg-gradient-to-r from-green-500 to-green-600 rounded-sm shadow-md border border-green-700 relative flex items-center justify-center shrink-0"
                        style={{ zIndex: maxBills - index }}
                    >
                        <span className="text-white text-xs font-bold">$</span>

                        {index < billsCollected && (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: [0, 1.2, 0] }}
                                transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
                                className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full"
                            >
                                <div className="w-full h-full bg-yellow-300 rounded-full animate-ping"></div>
                            </motion.div>
                        )}
                    </motion.div>
                ))}
            </div>

            {/* Savings percentage badge — stacked below bills on mobile,
                pinned to the right of the row on desktop. */}
            <div className={cn(
                "relative z-20 self-center sm:self-auto sm:absolute sm:top-1/2 sm:-translate-y-1/2",
                isRTL ? "sm:left-3" : "sm:right-3"
            )}>
                <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{
                        scale: isAnimating ? 1 : 0,
                        rotate: isAnimating ? 0 : -180
                    }}
                    transition={{ duration: 0.8, delay: 1.6 }}
                    className={cn(
                        "bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg whitespace-nowrap",
                        isRTL && "font-arabic"
                    )}
                >
                    {savingsAmount} {savedLabel}
                </motion.div>
            </div>
        </motion.div>
    );
}

export default function PricingInvestmentSection() {
    const { t, locale } = useTranslation('dental_veneers');
    const { isRTL } = useRTL();
    
    // Format percentage for RTL languages
    const formatPercentage = (percent: number) => {
        if (isRTL) {
            return `%${normalizeNumberString(percent.toLocaleString(resolveLocale(locale)))}`;
        }
        return `${percent}%`;
    };
    
    // Format savings amount for RTL languages
    const formatSavings = (amount: number, percentage: number) => {
        let formattedAmount;
        if (isRTL) {
            formattedAmount = formatNumberForLocale(amount, locale);
            formattedAmount = `${formattedAmount} $`;
        } else {
            formattedAmount = `$${amount.toLocaleString('en-US')}`;
        }
        const formattedPercentage = formatPercentage(percentage);
        
        if (isRTL) {
            return `کل صرفه‌جویی: ${formattedAmount} (${formattedPercentage})`;
        }
        return `Total Savings: ${formattedAmount} (${formattedPercentage})`;
    };
    
    const costFactors = [
        {
            icon: 'lets-icons:tooth',
            title: t('pricing.factors.material.title'),
            description: t('pricing.factors.material.description')
        },
        {
            icon: 'lsicon:number-filled',
            title: t('pricing.factors.number.title'),
            description: t('pricing.factors.number.description')
        },
        {
            icon: 'mdi:near-field-communication-tap',
            title: t('pricing.factors.complexity.title'),
            description: t('pricing.factors.complexity.description')
        },
        {
            icon: 'ic:baseline-biotech',
            title: t('pricing.factors.expertise.title'),
            description: t('pricing.factors.expertise.description')
        }
    ];
    const includedServices = t('pricing.included.services', { returnObjects: true }) as string[];

    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const introRef = useRef<HTMLParagraphElement>(null);
    const factorsRef = useRef<HTMLDivElement>(null);
    const includedRef = useRef<HTMLDivElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states with null checks
            if (titleRef.current) {
                gsap.set(titleRef.current, { opacity: 0, y: 50 });
            }
            if (introRef.current) {
                gsap.set(introRef.current, { opacity: 0, y: 50 });
            }
            if (ctaRef.current) {
                gsap.set(ctaRef.current, { opacity: 0, y: 50 });
            }
            if (includedRef.current) {
                gsap.set(includedRef.current, { opacity: 0, x: -30 });
            }

            if (factorsRef.current?.children) {
                gsap.set(factorsRef.current.children, { opacity: 0, y: 40, scale: 0.9 });
            }

            // Title animation with scroll trigger
            if (titleRef.current) {
                gsap.to(titleRef.current, {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: "top 80%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse",
                        scrub: false
                    }
                });
            }

            // Introduction animation
            if (introRef.current) {
                gsap.to(introRef.current, {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: introRef.current,
                        start: "top 75%",
                        end: "bottom 25%",
                        toggleActions: "play none none reverse",
                        scrub: false
                    }
                });
            }

            // Factors cards animation
            if (factorsRef.current?.children) {
                gsap.to(factorsRef.current.children, {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    stagger: 0.15,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: factorsRef.current,
                        start: "top 70%",
                        end: "bottom 30%",
                        toggleActions: "play none none reverse",
                        scrub: false
                    }
                });
            }

            // Included services animation
            if (includedRef.current) {
                gsap.to(includedRef.current, {
                    opacity: 1,
                    x: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: includedRef.current,
                        start: "top 75%",
                        end: "bottom 25%",
                        toggleActions: "play none none reverse",
                        scrub: false
                    }
                });
            }

            // CTA animation
            if (ctaRef.current) {
                gsap.to(ctaRef.current, {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ctaRef.current,
                        start: "top 75%",
                        end: "bottom 25%",
                        toggleActions: "play none none reverse",
                        scrub: false
                    }
                });
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const handleConsultationClick = () => {
        window.open('/book-appointment', '_blank');
    };

    return (
        <section
            id="pricing"
            ref={sectionRef}
            className="py-20 pb-0 bg-slate-50 dark:bg-navy-950 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gold/20 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className={cn(
                            "text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif",
                            isRTL && "font-arabic"
                        )}
                    >
                        {t('pricing.title')}
                    </h2>

                    <p
                        ref={introRef}
                        className={cn(
                            "text-xl text-slate-600 dark:text-slate-300 leading-relaxed",
                            isRTL && "font-arabic"
                        )}
                    >
                        {t('pricing.subtitle')}
                    </p>
                </div>

                {/* Factors Influencing Cost */}
                <div className="mb-16">
                    <div className="text-center mb-12">
                        <h3 className={cn(
                            "text-3xl font-bold text-navy dark:text-white mb-4",
                            isRTL && "font-arabic"
                        )}>
                            {t('pricing.factors.title')}
                        </h3>
                    </div>

                    <div ref={factorsRef} className="grid md:grid-cols-2 gap-8">
                        {(Array.isArray(costFactors) ? costFactors : []).map((factor, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ scale: 1.02, y: -5 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Card className="h-full bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-gold/50 transition-all duration-300 group">
                                    <CardContent className="p-6">
                                        <div className="relative">
                                            <div className={cn(
                                                "w-12 h-12 bg-gold rounded-xl flex items-center justify-center absolute top-0 group-hover:scale-110 transition-transform duration-300",
                                                isRTL ? "right-0" : "left-0"
                                            )}>
                                                <Icon icon={factor.icon} className="w-6 h-6 text-white" />
                                            </div>
                                            <div className={cn(
                                                "w-full",
                                                isRTL ? "pr-16" : "pl-16"
                                            )}>
                                                <h4 className={cn(
                                                    "text-xl font-bold text-navy dark:text-white mb-3 group-hover:text-gold dark:group-hover:text-gold transition-colors duration-300",
                                                    isRTL && "font-arabic text-right"
                                                )}>
                                                    {factor.title}
                                                </h4>
                                                <p className={cn(
                                                    "text-slate-600 dark:text-slate-300 leading-relaxed",
                                                    isRTL && "font-arabic text-right"
                                                )}>
                                                    {factor.description}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* What's Included*/}
                <div className="grid lg:grid-cols-1 px-4 md:px-8 lg:px-32 mb-16">
                    {/* What's Included */}
                    <div ref={includedRef}>
                        <Card className="h-full bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                            <CardContent className="p-8">
                                <div className={cn(
                                    "flex items-center justify-center gap-3 mb-6",
                                    isRTL && "flex-row-reverse"
                                )}>
                                    <div className="w-12 h-12 bg-gold rounded-xl flex items-center justify-center">
                                        <Icon icon="ic:baseline-request-quote" className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className={cn(
                                        "text-2xl font-bold text-navy dark:text-white",
                                        isRTL && "font-arabic"
                                    )}>
                                        {t('pricing.included.title')}
                                    </h3>
                                </div>

                                <p className={cn(
                                    "text-slate-600 dark:text-slate-300 mb-6 leading-relaxed",
                                    isRTL && "font-arabic"
                                )}>
                                    {t('pricing.included.description')}
                                </p>

                                <div className="space-y-3">
                                    {(Array.isArray(includedServices) ? includedServices : []).map((service, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: -20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.5, delay: index * 0.1 }}
                                            viewport={{ once: true }}
                                            className={cn(
                                                "relative w-full",
                                                isRTL ? "pr-6 text-right" : "pl-6"
                                            )}
                                        >
                                            <div className={cn(
                                                "w-2 h-2 bg-gold rounded-full absolute mt-2",
                                                isRTL ? "right-0" : "left-0"
                                            )}></div>
                                            <span className={cn(
                                                "text-slate-600 dark:text-slate-300 leading-relaxed block",
                                                isRTL && "font-arabic text-right"
                                            )}>
                                                {service}
                                            </span>
                                        </motion.div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Global Pricing Comparison */}
                <div className="mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <Card className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border border-white/20 dark:border-slate-700/30 overflow-hidden shadow-2xl">
                            <CardContent className="p-2 sm:p-8">
                                <div className="text-center mb-12 relative">
                                    {/* Background decorative elements */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-gold/5 via-transparent to-gold/5 rounded-full blur-3xl"></div>

                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.6, delay: 0.2 }}
                                        viewport={{ once: true }}
                                        className={cn(
                                            "relative inline-flex items-center gap-3 bg-gold/20 backdrop-blur-md text-gold dark:text-gold px-8 py-4 rounded-full text-sm font-bold border border-gold/30 shadow-lg mb-8",
                                            isRTL && "flex-row-reverse font-arabic"
                                        )}
                                    >
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                        >
                                            <Icon icon="material-symbols:savings-outline" className="w-6 h-6" />
                                        </motion.div>
                                        <span className="text-lg">{t('pricing.comparison.title')}</span>
                                        <motion.div
                                            animate={{ y: [-2, 2, -2] }}
                                            transition={{ duration: 1.5, repeat: Infinity }}
                                        >
                                            <Icon icon="material-symbols:trending-down" className="w-6 h-6" />
                                        </motion.div>
                                    </motion.div>

                                    <h3 className={cn(
                                        "text-4xl md:text-5xl font-bold pb-2 bg-gradient-to-r from-navy to-[#2A3B5A] dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-6",
                                        isRTL && "font-arabic"
                                    )}>
                                        {t('pricing.comparison.subtitle')}
                                    </h3>
                                    <p className={cn(
                                        "text-xl text-slate-600 dark:text-slate-300 leading-relaxed max-w-4xl mx-auto",
                                        isRTL && "font-arabic"
                                    )}>
                                        {t('pricing.comparison.description').split('incredible savings').map((part: string, index: number, array: string[]) => 
                                            index === array.length - 1 ? part : (
                                                <span key={index}>
                                                    {part}
                                                    <span className="font-bold text-gold dark:text-gold">
                                                        {t('pricing.comparison.incredibleSavings')}
                                                    </span>
                                                </span>
                                            )
                                        )}
                                    </p>
                                </div>

                                {/* Enhanced Comparison Cards Grid — min-w-0 on
                                    cells stops any wide descendant (e.g. the
                                    bill strip) from blowing out the grid. */}
                                <div className="grid lg:grid-cols-2 gap-8 mb-12 [&>*]:min-w-0">
                                    {/* Porcelain Veneers Comparison */}
                                    <motion.div
                                        initial={{ opacity: 0, x: -50 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.8, delay: 0.1 }}
                                        viewport={{ once: true }}
                                        className="group"
                                    >
                                        <div className="relative bg-white dark:bg-slate-800 rounded-3xl p-3 sm:p-8 border-2 border-slate-200 dark:border-slate-700 hover:border-gold/60 transition-all duration-500 hover:shadow-2xl hover:shadow-gold/20 group-hover:scale-[1.02]">
                                            {/* Floating badge — on mobile stays inside the card
                                                edge (right-3) so it doesn't clip; on sm+ keeps the
                                                original decorative overshoot (-right-4). */}
                                            <div className={cn(
                                                "absolute -top-3 sm:-top-4 bg-gold text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold shadow-lg transition-transform duration-300",
                                                isRTL ? "left-3 sm:-left-4" : "right-3 sm:-right-4"
                                            )}>
                                                {t('pricing.comparison.porcelain.label')}
                                            </div>

                                            <div className="relative mb-8">
                                                <motion.div
                                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                                    className={cn(
                                                        "w-12 h-12 sm:w-16 sm:h-16 bg-gold rounded-2xl flex items-center justify-center shadow-lg absolute top-0",
                                                        isRTL ? "right-0" : "left-0"
                                                    )}
                                                >
                                                    <Icon icon="material-symbols:diamond-outline" className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                                                </motion.div>
                                                <div className={cn(
                                                    "w-full",
                                                    isRTL ? "pr-16 sm:pr-20 text-right" : "pl-16 sm:pl-20"
                                                )}>
                                                    <h4 className={cn(
                                                        "text-xl sm:text-2xl font-bold text-navy dark:text-white mb-1",
                                                        isRTL && "font-arabic"
                                                    )}>{t('pricing.comparison.porcelain.title')}</h4>
                                                    <p className={cn(
                                                        "text-sm sm:text-base text-slate-600 dark:text-slate-300 font-medium",
                                                        isRTL && "font-arabic"
                                                    )}>{t('pricing.comparison.porcelain.quality')}</p>
                                                </div>
                                            </div>

                                            {/* Enhanced Price Comparison */}
                                            <div className="space-y-6">
                                                {/* {t('pricing.comparison.locations.western')} */}
                                                <div className="relative bg-gradient-to-r from-red-100 to-red-50 dark:from-red-950/30 dark:to-red-900/20 rounded-2xl p-4 sm:p-6 border border-red-200/50 dark:border-red-800/30 overflow-hidden">
                                                    <div className="absolute top-0 right-0 w-32 h-32 bg-red-200/30 rounded-full blur-2xl"></div>
                                                    <div className="relative">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <span className="text-sm font-bold text-red-700 dark:text-red-400 uppercase tracking-wider">{t('pricing.comparison.locations.western')}</span>
                                                            <motion.div animate={{ y: [-1, 1, -1] }} transition={{ duration: 2, repeat: Infinity }}>
                                                                <Icon icon="material-symbols:trending-up" className="w-5 h-5 text-red-500" />
                                                            </motion.div>
                                                        </div>
                                                        <div className="text-2xl sm:text-3xl font-black text-red-800 dark:text-red-300 mb-2">
                                                            <AnimatedPriceRange
                                                                min={1000}
                                                                max={2500}
                                                                isRTL={isRTL}
                                                                locale={locale}
                                                            />
                                                        </div>
                                                        <div className="text-sm text-red-600 dark:text-red-400 font-medium">{t('pricing.comparison.pricing.perToothWestern')}</div>
                                                    </div>
                                                </div>

                                                {/* Versus Icon */}
                                                <div className="flex justify-center">
                                                    <motion.div
                                                        animate={{ scale: [1, 1.1, 1] }}
                                                        transition={{ duration: 2, repeat: Infinity }}
                                                        className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg"
                                                    >
                                                        <span className="text-white font-bold text-sm">VS</span>
                                                    </motion.div>
                                                </div>

                                                {/* DentaWista */}
                                                <div className="relative bg-gradient-to-r from-green-100 to-emerald-50 dark:from-green-950/30 dark:to-emerald-900/20 rounded-2xl p-4 sm:p-6 border border-green-200/50 dark:border-green-800/30 overflow-hidden">
                                                    {/* Animated savings badge — desktop only;
                                                        mobile gets the larger badge below the bills instead. */}
                                                    <motion.div
                                                        className={cn(
                                                            "hidden sm:block absolute top-3",
                                                            isRTL ? "left-3" : "right-3"
                                                        )}
                                                        animate={{ scale: [1, 1.05, 1], rotate: [0, 5, 0] }}
                                                        transition={{ duration: 3, repeat: Infinity }}
                                                    >
                                                        <div className={cn(
                                                            "bg-gold text-white text-xs font-black px-3 py-1 rounded-full shadow-lg",
                                                            isRTL && "font-arabic"
                                                        )}>
                                                            {isRTL ? `%٨٠ صرفه‌جویی` : '80% Savings'}
                                                        </div>
                                                    </motion.div>
                                                    <div className="absolute top-0 left-0 w-32 h-32 bg-green-200/30 rounded-full blur-2xl"></div>
                                                    <div className="relative">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <span className="text-sm font-bold text-green-700 dark:text-green-400 uppercase tracking-wider">DentaWista, Turkey</span>
                                                            <motion.div animate={{ y: [1, -1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                                                                <Icon icon="material-symbols:trending-down" className="w-5 h-5 text-green-500" />
                                                            </motion.div>
                                                        </div>
                                                        <div className="text-2xl sm:text-3xl font-black text-gold mb-2">
                                                            <AnimatedPriceRange
                                                                min={150}
                                                                max={350}
                                                                isRTL={isRTL}
                                                                locale={locale}
                                                            />
                                                        </div>
                                                        <div className="text-sm text-green-600 dark:text-green-400 font-medium">{t('pricing.comparison.pricing.perToothPremium')}</div>

                                                        {/* Savings Progress Bar */}
                                                        <div className="mt-4">
                                                            <div className="flex justify-between text-xs mb-2">
                                                                <span className="text-green-700 dark:text-green-400 font-medium">{t('pricing.comparison.savings.label')}</span>
                                                                <span className={cn(
                                                                    "text-green-700 dark:text-green-400 font-bold",
                                                                    isRTL && "font-arabic"
                                                                )}>{formatPercentage(80)}</span>
                                                            </div>
                                                            <AnimatedMoneySavings
                                                                percentage={80}
                                                                savingsAmount={formatPercentage(80)}
                                                                isRTL={isRTL}
                                                                savedLabel={t('pricing.comparison.savings.saved')}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Advantage Card */}
                                                <motion.div
                                                    whileHover={{ scale: 1.02 }}
                                                    className="bg-gradient-to-r from-gold/10 via-gold/5 to-gold/10 rounded-2xl p-6 border border-gold/20"
                                                >
                                                    <div className={cn(
                                                        "flex items-start gap-3",
                                                        isRTL && "flex-row-reverse"
                                                    )}>
                                                        <Icon icon="material-symbols:verified" className="w-6 h-6 text-gold dark:text-gold mt-1 shrink-0" />
                                                        <div className={cn(isRTL && "text-right")}>
                                                            <div className={cn(
                                                                "font-bold text-gold dark:text-gold text-lg mb-2",
                                                                isRTL && "font-arabic"
                                                            )}>{t('pricing.comparison.promise.title')}</div>
                                                            <div className={cn(
                                                                "text-slate-600 dark:text-slate-300",
                                                                isRTL && "font-arabic"
                                                            )}>{t('pricing.comparison.promise.description')}</div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* {t('pricing.comparison.composite.title')} Comparison */}
                                    <motion.div
                                        initial={{ opacity: 0, x: 50 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.8, delay: 0.2 }}
                                        viewport={{ once: true }}
                                        className="group"
                                    >
                                        <div className="relative bg-white dark:bg-slate-800 rounded-3xl p-3 sm:p-8 border-2 border-slate-200 dark:border-slate-700 hover:border-[#4F46E5]/60 transition-all duration-500 hover:shadow-2xl hover:shadow-[#4F46E5]/20 group-hover:scale-[1.02]">
                                            {/* Floating badge — mobile stays inside the card edge
                                                so it doesn't clip; desktop keeps the overshoot. */}
                                            <div className={cn(
                                                "absolute -top-3 sm:-top-4 bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold shadow-lg transition-transform duration-300",
                                                isRTL ? "left-3 sm:-left-4" : "right-3 sm:-right-4"
                                            )}>
                                                {t('pricing.comparison.composite.label')}
                                            </div>

                                            <div className="relative mb-8">
                                                <motion.div
                                                    whileHover={{ scale: 1.1, rotate: -5 }}
                                                    className={cn(
                                                        "w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] rounded-2xl flex items-center justify-center shadow-lg absolute top-0",
                                                        isRTL ? "right-0" : "left-0"
                                                    )}
                                                >
                                                    <Icon icon="material-symbols:speed-outline" className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                                                </motion.div>
                                                <div className={cn(
                                                    "w-full",
                                                    isRTL ? "pr-16 sm:pr-20 text-right" : "pl-16 sm:pl-20"
                                                )}>
                                                    <h4 className={cn(
                                                        "text-xl sm:text-2xl font-bold text-navy dark:text-white mb-1",
                                                        isRTL && "font-arabic"
                                                    )}>{t('pricing.comparison.composite.title')}</h4>
                                                    <p className={cn(
                                                        "text-sm sm:text-base text-slate-600 dark:text-slate-300 font-medium",
                                                        isRTL && "font-arabic"
                                                    )}>{t('pricing.comparison.composite.quality')}</p>
                                                </div>
                                            </div>

                                            {/* Enhanced Price Comparison */}
                                            <div className="space-y-6">
                                                {/* {t('pricing.comparison.locations.western')} */}
                                                <div className="relative bg-gradient-to-r from-red-100 to-red-50 dark:from-red-950/30 dark:to-red-900/20 rounded-2xl p-4 sm:p-6 border border-red-200/50 dark:border-red-800/30 overflow-hidden">
                                                    <div className="absolute top-0 right-0 w-32 h-32 bg-red-200/30 rounded-full blur-2xl"></div>
                                                    <div className="relative">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <span className="text-sm font-bold text-red-700 dark:text-red-400 uppercase tracking-wider">{t('pricing.comparison.locations.western')}</span>
                                                            <motion.div animate={{ y: [-1, 1, -1] }} transition={{ duration: 2, repeat: Infinity }}>
                                                                <Icon icon="material-symbols:trending-up" className="w-5 h-5 text-red-500" />
                                                            </motion.div>
                                                        </div>
                                                        <div className="text-2xl sm:text-3xl font-black text-red-800 dark:text-red-300 mb-2">
                                                            <AnimatedPriceRange
                                                                min={300}
                                                                max={800}
                                                                isRTL={isRTL}
                                                                locale={locale}
                                                            />
                                                        </div>
                                                        <div className="text-sm text-red-600 dark:text-red-400 font-medium">{t('pricing.comparison.pricing.perToothWestern')}</div>
                                                    </div>
                                                </div>

                                                {/* Versus Icon */}
                                                <div className="flex justify-center">
                                                    <motion.div
                                                        animate={{ scale: [1, 1.1, 1] }}
                                                        transition={{ duration: 2, repeat: Infinity }}
                                                        className="w-12 h-12 bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] rounded-full flex items-center justify-center shadow-lg"
                                                    >
                                                        <span className="text-white font-bold text-sm">VS</span>
                                                    </motion.div>
                                                </div>

                                                {/* DentaWista */}
                                                <div className="relative bg-gradient-to-r from-green-100 to-emerald-50 dark:from-green-950/30 dark:to-emerald-900/20 rounded-2xl p-4 sm:p-6 border border-green-200/50 dark:border-green-800/30 overflow-hidden">
                                                    {/* Animated savings badge — desktop only;
                                                        mobile gets the larger badge below the bills instead. */}
                                                    <motion.div
                                                        className={cn(
                                                            "hidden sm:block absolute top-3",
                                                            isRTL ? "left-3" : "right-3"
                                                        )}
                                                        animate={{ scale: [1, 1.05, 1], rotate: [0, -5, 0] }}
                                                        transition={{ duration: 3, repeat: Infinity }}
                                                    >
                                                        <div className={cn(
                                                            "bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] text-white text-xs font-black px-3 py-1 rounded-full shadow-lg",
                                                            isRTL && "font-arabic"
                                                        )}>
                                                            {isRTL ? `%٧٥ صرفه‌جویی` : '75% Savings'}
                                                        </div>
                                                    </motion.div>
                                                    <div className="absolute top-0 left-0 w-32 h-32 bg-green-200/30 rounded-full blur-2xl"></div>
                                                    <div className="relative">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <span className="text-sm font-bold text-green-700 dark:text-green-400 uppercase tracking-wider">DentaWista, Turkey</span>
                                                            <motion.div animate={{ y: [1, -1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                                                                <Icon icon="material-symbols:trending-down" className="w-5 h-5 text-green-500" />
                                                            </motion.div>
                                                        </div>
                                                        <div className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] bg-clip-text text-transparent mb-2">
                                                            <AnimatedPriceRange
                                                                min={80}
                                                                max={200}
                                                                isRTL={isRTL}
                                                                locale={locale}
                                                            />
                                                        </div>
                                                        <div className="text-sm text-green-600 dark:text-green-400 font-medium">{t('pricing.comparison.pricing.perToothStandards')}</div>

                                                        {/* Savings Progress Bar */}
                                                        <div className="mt-4">
                                                            <div className="flex justify-between text-xs mb-2">
                                                                <span className="text-green-700 dark:text-green-400 font-medium">{t('pricing.comparison.savings.label')}</span>
                                                                <span className={cn(
                                                                    "text-green-700 dark:text-green-400 font-bold",
                                                                    isRTL && "font-arabic"
                                                                )}>{formatPercentage(75)}</span>
                                                            </div>
                                                            <AnimatedMoneySavings
                                                                percentage={75}
                                                                savingsAmount={formatPercentage(75)}
                                                                isRTL={isRTL}
                                                                savedLabel={t('pricing.comparison.savings.saved')}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Advantage Card */}
                                                <motion.div
                                                    whileHover={{ scale: 1.02 }}
                                                    className="bg-gradient-to-r from-[#4F46E5]/10 via-[#7C3AED]/5 to-[#4F46E5]/10 rounded-2xl p-6 border border-[#4F46E5]/20"
                                                >
                                                    <div className={cn(
                                                        "flex items-start gap-3",
                                                        isRTL && "flex-row-reverse"
                                                    )}>
                                                        <motion.div
                                                            animate={{ scale: [1, 1.1, 1] }}
                                                            transition={{ duration: 2, repeat: Infinity }}
                                                        >
                                                            <Icon icon="material-symbols:flash-on" className="w-6 h-6 text-[#4F46E5] dark:text-purple-500 mt-1 shrink-0" />
                                                        </motion.div>
                                                        <div className={cn(isRTL && "text-right")}>
                                                            <div className={cn(
                                                                "font-bold text-[#4F46E5] dark:text-purple-500 text-lg mb-2",
                                                                isRTL && "font-arabic"
                                                            )}>{t('pricing.comparison.composite.excellence')}</div>
                                                            <div className={cn(
                                                                "text-slate-600 dark:text-slate-300",
                                                                isRTL && "font-arabic"
                                                            )}>{t('pricing.comparison.composite.excellenceDesc')}</div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>

                                {/* Enhanced Key Advantages Grid */}
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.3 }}
                                    viewport={{ once: true }}
                                    className="grid md:grid-cols-3 gap-8 mb-12"
                                >
                                    {(Array.isArray(t('pricing.valuePropositions', { returnObjects: true })) ? 
                                        (t('pricing.valuePropositions', { returnObjects: true }) as any[]) : 
                                        []
                                    ).map((valueItem: any, index: number) => {
                                        const iconMap = [
                                            "material-symbols:high-quality",
                                            "material-symbols:package-2-outline", 
                                            "material-symbols:verified-user-outline"
                                        ];
                                        const gradientMap = [
                                            { gradient: "from-gold to-gold-600", bgGradient: "from-gold/5 to-gold/5" },
                                            { gradient: "from-[#10B981] to-[#059669]", bgGradient: "from-[#10B981]/5 to-[#059669]/5" },
                                            { gradient: "from-[#3B82F6] to-[#1D4ED8]", bgGradient: "from-[#3B82F6]/5 to-[#1D4ED8]/5" }
                                        ];
                                        const item = {
                                            icon: iconMap[index],
                                            title: valueItem.title,
                                            description: valueItem.description,
                                            ...gradientMap[index]
                                        };
                                        
                                        return (
                                        <motion.div
                                            key={index}
                                            whileHover={{ scale: 1.05, y: -5 }}
                                            transition={{ duration: 0.3 }}
                                            className={`text-center p-8 bg-gradient-to-br ${item.bgGradient} backdrop-blur-xs rounded-3xl border border-white/20 dark:border-gray-800/30 shadow-lg hover:shadow-xl transition-all duration-300`}
                                        >
                                            <motion.div
                                                className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}
                                                whileHover={{ rotate: 360 }}
                                                transition={{ duration: 0.6 }}
                                            >
                                                <Icon icon={item.icon} className="w-10 h-10 text-white" />
                                            </motion.div>
                                            <h5 className={cn(
                                                "font-bold text-navy dark:text-white mb-3 text-lg",
                                                isRTL && "font-arabic"
                                            )}>{item.title}</h5>
                                            <p className={cn(
                                                "text-sm text-slate-600 dark:text-slate-300 leading-relaxed",
                                                isRTL && "font-arabic"
                                            )}>{item.description}</p>
                                        </motion.div>
                                        );
                                    })}
                                </motion.div>

                                {/* Enhanced Savings Calculator */}
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.8, delay: 0.4 }}
                                    viewport={{ once: true }}
                                    className="relative bg-gradient-to-br from-navy via-navy-700 to-navy rounded-3xl p-10 text-center text-white overflow-hidden shadow-2xl"
                                >
                                    {/* Animated background elements */}
                                    <div className="absolute inset-0">
                                        <div className="absolute top-0 left-0 w-72 h-72 bg-gold/10 rounded-full blur-3xl animate-pulse"></div>
                                        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                                    </div>

                                    <div className="relative z-10">
                                        <motion.div
                                            animate={{ rotate: [0, 360] }}
                                            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                                        >
                                            <Icon icon="material-symbols:calculate" className="w-16 h-16 text-gold mx-auto mb-6" />
                                        </motion.div>
                                        <h4 className={cn(
                                            "text-3xl md:text-4xl font-bold mb-8 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent",
                                            isRTL && "font-arabic"
                                        )}>
                                            {t('pricing.calculator.title')}
                                        </h4>

                                        <div className="grid md:grid-cols-3 gap-8 items-center mb-8">
                                            <motion.div
                                                initial={{ opacity: 0, x: -30 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.6, delay: 0.5 }}
                                                viewport={{ once: true }}
                                                className="bg-red-500/20 backdrop-blur-xs rounded-2xl p-6 border border-red-400/30"
                                            >
                                                <div className="text-4xl font-black text-red-400 mb-2">
                                                    <AnimatedPrice
                                                        value={12000}
                                                        isRTL={isRTL}
                                                        locale={locale}
                                                    />
                                                </div>
                                                <div className={cn(
                                                    "text-sm opacity-90",
                                                    isRTL && "font-arabic"
                                                )}>{t('pricing.calculator.usExample')}</div>
                                            </motion.div>

                                            <motion.div
                                                className="flex items-center justify-center"
                                                animate={{ x: [0, 10, 0] }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                            >
                                                <div className="bg-white/10 backdrop-blur-xs rounded-full p-4">
                                                    <Icon icon="material-symbols:arrow-right-alt" className="w-8 h-8 text-gold" />
                                                </div>
                                            </motion.div>

                                            <motion.div
                                                initial={{ opacity: 0, x: 30 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.6, delay: 0.7 }}
                                                viewport={{ once: true }}
                                                className="bg-green-500/20 backdrop-blur-xs rounded-2xl p-6 border border-green-400/30"
                                            >
                                                <div className="text-4xl font-black text-gold mb-2">
                                                    <AnimatedPrice
                                                        value={2400}
                                                        isRTL={isRTL}
                                                        locale={locale}
                                                    />
                                                </div>
                                                <div className={cn(
                                                    "text-sm opacity-90",
                                                    isRTL && "font-arabic"
                                                )}>{t('pricing.calculator.dentawistaExample')}</div>
                                            </motion.div>
                                        </div>

                                        <motion.div
                                            className={cn(
                                                "inline-flex items-center gap-3 bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-4 rounded-full shadow-xl",
                                                isRTL && "flex-row-reverse"
                                            )}
                                            whileHover={{ scale: 1.05 }}
                                            animate={{ y: [0, -5, 0] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                        >
                                            <Icon icon="material-symbols:savings" className="w-6 h-6" />
                                            <span className={cn(
                                                "font-black text-xl",
                                                isRTL && "font-arabic"
                                            )}>{formatSavings(9600, 80)}</span>
                                        </motion.div>

                                        <p className={cn(
                                            "text-sm opacity-75 mt-6 max-w-2xl mx-auto",
                                            isRTL && "font-arabic"
                                        )}>
                                            {t('pricing.calculator.vacation')}
                                        </p>
                                    </div>
                                </motion.div>

                                {/* Enhanced Disclaimer */}
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.5 }}
                                    viewport={{ once: true }}
                                    className={cn(
                                        "text-sm text-slate-600 dark:text-slate-300 mt-10 text-center italic max-w-4xl mx-auto bg-gradient-to-r from-transparent via-gray-100/50 dark:via-gray-800/50 to-transparent p-4 rounded-xl",
                                        isRTL && "font-arabic"
                                    )}
                                >
                                    {t('pricing.calculator.disclaimer')}
                                </motion.p>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </div>

            {/* Full Width Call to Action */}
            <div ref={ctaRef} className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mt-16">
                <div className="bg-gradient-to-br from-navy to-navy-950 py-10 px-8 relative overflow-hidden">
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gold/20 rounded-full blur-2xl"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-gold/20 rounded-full blur-xl"></div>

                    <div className="relative z-10 text-center max-w-4xl mx-auto">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center">
                                <Icon icon="material-symbols:calendar-month-outline" className="w-6 h-6 text-white" />
                            </div>
                        </div>

                        <h3 className={cn(
                            "text-2xl md:text-3xl font-bold text-white mb-3",
                            isRTL && "font-arabic"
                        )}>
                            {t('pricing.cta.title')}
                        </h3>

                        <p className={cn(
                            "text-lg text-slate-300 leading-relaxed mb-6 max-w-2xl mx-auto",
                            isRTL && "font-arabic"
                        )}>
                            {t('pricing.cta.description')}
                        </p>

                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="mb-3"
                        >
                            <Button
                                onClick={handleConsultationClick}
                                size="lg"
                                className={cn(
                                    "bg-gold hover:bg-gold-600 text-navy px-4 py-3 sm:px-8 sm:py-4 text-sm sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-gold/50 transition-all duration-300 border-2 border-gold hover:border-gold-600 inline-flex items-center justify-center gap-2 sm:gap-3 whitespace-normal sm:whitespace-nowrap leading-tight",
                                    isRTL && "flex-row-reverse font-arabic"
                                )}
                            >
                                <Icon
                                    icon="material-symbols:calendar-month-outline"
                                    className="w-4 h-4 sm:w-5 sm:h-5 shrink-0"
                                />
                                <span>{t('pricing.cta.button')}</span>
                                <Icon
                                    icon="material-symbols:arrow-right-alt"
                                    className={cn(
                                        "w-4 h-4 sm:w-5 sm:h-5 shrink-0",
                                        isRTL && "rotate-180"
                                    )}
                                />
                            </Button>
                        </motion.div>

                        <p className={cn(
                            "text-slate-300 text-sm opacity-80",
                            isRTL && "font-arabic"
                        )}>
                            {t('pricing.cta.disclaimer')}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
} 
